// store global types
import type { TCreateSlice } from "@/store";

import type { TCoAMappingsAccountDTO } from "./TCoAMappingsAccountDTO";
import type {
    TAccountMappingTableDTO,
    AssetType,
    TAccountMappingTableRow,
    TSubcategoryInfo,
} from "./TAccountMappingTableDTO";

export const selectVisibleAccountMappingTableData = ({
    accountMappingTableData,
    alteredCustomAccounts,
    customAccounts,
    selectedAssetTypes,
    selectedMappingStatuses,
}: {
    accountMappingTableData: TAccountMappingTableDTO[] | null;
    alteredCustomAccounts: TAlteredCustomAccount[];
    customAccounts: TCoAMappingsAccountDTO[] | null;
    selectedAssetTypes: AssetType[];
    selectedMappingStatuses: MappingStatus[];
}): TAccountMappingTableRow[] | null => {
    if (!accountMappingTableData || !customAccounts) {
        return null;
    }

    const mappedRows = accountMappingTableData.map(item => {
        const alteredAccount = alteredCustomAccounts.find(
            account => account.accountCode === item.code,
        );

        if (!alteredAccount) {
            return item;
        }

        const alteredAccountCustomAccount = customAccounts.find(
            account => account.code === alteredAccount.mappedAccountCode.to,
        );
        if (
            alteredAccount.mappedAccountCode.to !== null &&
            !alteredAccountCustomAccount
        ) {
            console.error(
                `Custom account ${alteredAccount.mappedAccountCode.to} not found`,
            );
            return item;
        }

        const mappedAccount = alteredAccount.mappedAccountCode.to;
        const mappedSection = alteredAccountCustomAccount
            ? alteredAccountCustomAccount.section
            : null;
        const mappedSubcategory = alteredAccountCustomAccount
            ? alteredAccountCustomAccount.subcategory
            : null;

        return {
            ...item,
            mappedAccountCode: mappedAccount,
            mappedSection,
            mappedSubcategory,
        };
    });

    // Apply MappingStatus and AssetType filters using provided selection
    let filteredRows = mappedRows;
    if (selectedMappingStatuses.length === 0) {
        // Explicitly show no rows if nothing is selected
        filteredRows = [];
    } else if (selectedMappingStatuses.length < 2) {
        // If both statuses are selected, do not filter
        filteredRows = filteredRows.filter(row =>
            selectedMappingStatuses.includes(
                row.mappedAccountCode !== null ? "mapped" : "unmapped",
            ),
        );
    }
    // Combine asset type filtering with mapping to rows with subcategories
    if (selectedAssetTypes.length === 0) {
        // Explicitly show no rows if nothing is selected
        return [];
    }
    const rows: TAccountMappingTableRow[] = filteredRows
        .map(row => {
            const { assetTypes: rowAssetTypes, ...rest } = row;
            const subcategoryRecord: Record<string, TSubcategoryInfo> = {};
            for (const [assetType, data] of Object.entries(rowAssetTypes)) {
                if (
                    selectedAssetTypes.includes(assetType as AssetType) &&
                    data.subcategory
                ) {
                    if (!(data.subcategory in subcategoryRecord)) {
                        subcategoryRecord[data.subcategory] = {
                            name: data.subcategory,
                            isNonOperating: !data.includeInSummarisedDoc,
                        };
                    }
                }
            }
            return {
                ...rest,
                subcategories: Object.values(subcategoryRecord),
            };
        })
        .filter(row => row.subcategories.length > 0);

    return rows;
};

export const selectAvailableAssetTypes = ({
    accountMappingTableData,
}: {
    accountMappingTableData: TAccountMappingTableDTO[] | null;
}): AssetType[] => {
    if (!accountMappingTableData) return [];
    return Array.from(
        new Set(
            accountMappingTableData.flatMap(item =>
                Object.keys(item.assetTypes),
            ),
        ),
    ) as AssetType[];
};

type TActionsPublicCustomChartOfAccountsMappings = {
    resetStateCustomChartOfAccountsMappings: () => void;
    changeCustomAccountMapping: (options: {
        accountCode: string;
        newMappedAccountCode: string | null;
        mappingTable: TAccountMappingTableDTO[];
        customAccounts: TCoAMappingsAccountDTO[];
    }) => void;
    selectAssetTypes: (values: AssetType[]) => void;
    selectMappingStatuses: (values: MappingStatus[]) => void;
    setAlteredCustomAccounts: (accounts: TAlteredCustomAccount[]) => void;
};

export type TAlteredCustomAccount = {
    accountCode: string;
    mappedAccountCode: {
        from: string | null;
        to: string | null;
    };
};

type MappingStatus = "mapped" | "unmapped";

type TDerivedStateCustomChartOfAccountsMappings = {
    selectVisibleAccountMappingTableData: (params: {
        alteredCustomAccounts: TAlteredCustomAccount[];
        accountMappingTableData: TAccountMappingTableDTO[] | null;
        customAccounts: TCoAMappingsAccountDTO[] | null;
        selectedMappingStatuses: MappingStatus[];
        selectedAssetTypes: AssetType[];
    }) => TAccountMappingTableRow[] | null;
    selectAvailableAssetTypes: (params: {
        accountMappingTableData: TAccountMappingTableDTO[] | null;
    }) => AssetType[];
};

export type TStateCustomChartOfAccountsMappings = {
    alteredCustomAccounts: TAlteredCustomAccount[];
    selectedAssetTypes: AssetType[];
    selectedMappingStatuses: MappingStatus[];
};

export type TSliceCustomChartOfAccountsMappings =
    TStateCustomChartOfAccountsMappings &
        TActionsPublicCustomChartOfAccountsMappings &
        TDerivedStateCustomChartOfAccountsMappings;

export const initialStateCustomChartOfAccountsMappings = {
    alteredCustomAccounts: [],
    selectedAssetTypes: ["multifamily"],
    selectedMappingStatuses: ["mapped", "unmapped"],
} as const satisfies TStateCustomChartOfAccountsMappings;

export const createSliceCustomChartOfAccountsMappings: TCreateSlice<
    TSliceCustomChartOfAccountsMappings
> = () => (set, get) => {
    const actionsPublic: TActionsPublicCustomChartOfAccountsMappings = {
        resetStateCustomChartOfAccountsMappings: () => {
            set(
                JSON.parse(
                    JSON.stringify(initialStateCustomChartOfAccountsMappings),
                ) as typeof initialStateCustomChartOfAccountsMappings,
                false,
                "customChartOfAccountsMappings/resetStateCustomChartOfAccountsMappings",
            );
        },
        changeCustomAccountMapping: ({
            accountCode,
            newMappedAccountCode,
            mappingTable,
            customAccounts,
        }) => {
            const state = get();

            const row = mappingTable.find(row => row.code === accountCode);
            if (!row) {
                console.error(
                    `No account ${accountCode} found in loaded default accounts`,
                );
                return;
            }

            let newMappedAccount = null;
            if (newMappedAccountCode !== null) {
                newMappedAccount = customAccounts.find(
                    customAccount =>
                        customAccount.code === newMappedAccountCode,
                );

                if (!newMappedAccount) {
                    console.error(
                        `No account ${newMappedAccountCode} found in loaded custom accounts`,
                    );
                    return;
                }
            }

            const isAccountAlreadyAltered = state.alteredCustomAccounts.some(
                alteredCustomAccount =>
                    alteredCustomAccount.accountCode === accountCode,
            );

            const newAlteredCustomAccountsEntry = {
                accountCode,
                mappedAccountCode: {
                    from: row.mappedAccountCode,
                    to: newMappedAccount ? newMappedAccount.code : null,
                },
            };

            const mappingWasRevertedToTheOriginalOne =
                newAlteredCustomAccountsEntry.mappedAccountCode.from ===
                newAlteredCustomAccountsEntry.mappedAccountCode.to;

            if (mappingWasRevertedToTheOriginalOne) {
                const newAlteredCustomAccounts =
                    state.alteredCustomAccounts.filter(
                        alteredCustomAccount =>
                            alteredCustomAccount.accountCode !== accountCode,
                    );
                set(
                    {
                        alteredCustomAccounts: newAlteredCustomAccounts,
                    },
                    false,
                    "customChartOfAccountsMappings/changeCustomAccountMapping",
                );
            } else {
                const newAlteredCustomAccounts: TAlteredCustomAccount[] =
                    isAccountAlreadyAltered
                        ? state.alteredCustomAccounts.map(
                              alteredCustomAccount =>
                                  alteredCustomAccount.accountCode ===
                                  accountCode
                                      ? newAlteredCustomAccountsEntry
                                      : alteredCustomAccount,
                          )
                        : [
                              ...state.alteredCustomAccounts,
                              newAlteredCustomAccountsEntry,
                          ];

                set(
                    {
                        alteredCustomAccounts: newAlteredCustomAccounts,
                    },
                    false,
                    "customChartOfAccountsMappings/changeCustomAccountMapping",
                );
            }
        },
        selectAssetTypes: values => {
            set(
                { selectedAssetTypes: values },
                false,
                "customChartOfAccountsMappings/selectAssetTypes",
            );
        },
        selectMappingStatuses: values => {
            set(
                { selectedMappingStatuses: values },
                false,
                "customChartOfAccountsMappings/selectMappingStatuses",
            );
        },
        setAlteredCustomAccounts: accounts => {
            set(
                { alteredCustomAccounts: accounts },
                false,
                "customChartOfAccountsMappings/setAlteredCustomAccounts",
            );
        },
    };

    return {
        ...initialStateCustomChartOfAccountsMappings,
        ...actionsPublic,
        selectVisibleAccountMappingTableData,
        selectAvailableAssetTypes,
    };
};
